import { useEffect, useState } from 'react';
import { FaPlus } from 'react-icons/fa';
import { FiInbox } from 'react-icons/fi';

import { useRequestWithPagination } from '../../../hooks/useRequestWithPagination';
import { AdminCourse } from '../../../models/CourseModels';
import { useAdminApi } from '../Admin/hooks/useAdminApi';
import { useAdminDelete } from '../Admin/hooks/useAdminDelete';
import { useToast } from '../../../hooks/toast';

import CoursesTable from './components/CoursesTable';
import CourseFilters from './components/CourseFilters';
import Button from '../../../components/Atoms/Button';
import { Modal } from '../../../components/Atoms/Modal';
import { PageHeader, EmptyState, ErrorState, SkeletonTable, Confirm } from '../../../components/Admin';

import { ListContainer } from '../../_layout/AdminLayout.styles';
import AddCourse from './components/AddCourse';
import { useCourseListFilter } from './hooks/useCourseListFilter';

const AdminCourses = () => {
  const {
    data,
    currentPage,
    maxPage,
    isLoading,
    setIsLoading,
    setData,
    setMaxPage,
    setCurrentPage,
  } = useRequestWithPagination<AdminCourse[]>([]);

  const { fetchCourses } = useAdminApi();
  const { addToast } = useToast();
  const { deleteItem, deletingId } = useAdminDelete();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingCourse, setPendingCourse] = useState<AdminCourse | null>(null);
  const [editingCourse, setEditingCourse] = useState<AdminCourse | null>(null);

  const { search, status, filtered, onSearchChange, onStatusChange } =
    useCourseListFilter(data);

  // `pages` vem da Lambda como QUANTIDADE de páginas (ceil(total/50)), e a
  // primeira é a 0: há mais enquanto a próxima ainda for menor que o total.
  const showFetchMoreBtn = !!data.length && currentPage + 1 < maxPage;

  const handleModalState = () => setOpenModal(prevState => !prevState);

  const handleDeactivate = (course: AdminCourse) => setPendingCourse(course);

  const confirmDeactivate = async () => {
    const course = pendingCourse;
    setPendingCourse(null);
    if (!course) return;

    const done = await deleteItem({
      entity: 'course',
      id: course.courseid,
      successTitle: 'Curso inativado',
    });

    // Continua na lista, agora como Inativo (o filtro "Ativos" o esconde).
    if (done) {
      setData(courses =>
        courses.map(item =>
          item.courseid === course.courseid ? { ...item, status: 'Inactive' } : item,
        ),
      );
    }
  };

  const fetchMore = async () => {
    const nextPage = currentPage + 1;
    if (nextPage >= maxPage) {
      return;
    }

    setIsLoading(true);
    try {
      const newPayments = await fetchCourses(nextPage);
      setData(oldPayments => oldPayments.concat(newPayments.data.courses ?? []));
      if (newPayments.data.pages !== maxPage) {
        setMaxPage(newPayments.data.pages);
      }
      setCurrentPage(nextPage);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao carregar cursos',
        description: 'Não foi possível buscar a lista. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await fetchCourses(0);
      setMaxPage(result.data.pages ?? 0);
      // Os inativos vêm junto de propósito: o filtro de status decide o que mostrar.
      setData(result.data.courses ?? []);
      setCurrentPage(0);
    } catch {
      setLoadError('Não foi possível buscar a lista de cursos.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showInitialLoading = isLoading && data.length === 0 && !loadError;

  return (
    <>
      <PageHeader
        title="Meus Cursos"
        subtitle="Crie, consulte e inative os cursos da escola."
        actions={
          <Button onClick={handleModalState}>
            <FaPlus />
            Novo Curso
          </Button>
        }
      />

      <ListContainer>
        {showInitialLoading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : loadError ? (
          <ErrorState message={loadError} onRetry={fetchData} />
        ) : data.length === 0 ? (
          <EmptyState
            icon={<FiInbox size={40} />}
            title="Nenhum curso cadastrado"
            description="Crie o primeiro curso da escola para começar."
            action={
              <Button onClick={handleModalState}>
                <FaPlus />
                Criar curso
              </Button>
            }
          />
        ) : (
          <>
            <CourseFilters
              search={search}
              status={status}
              onSearchChange={onSearchChange}
              onStatusChange={onStatusChange}
            />
            {filtered.length === 0 ? (
              <EmptyState
                icon={<FiInbox size={40} />}
                title="Nenhum curso encontrado"
                description="Tente ajustar a busca ou o filtro de status."
              />
            ) : (
              <CoursesTable
                data={filtered}
                onDeactivate={handleDeactivate}
                onEdit={setEditingCourse}
                deletingId={deletingId}
              />
            )}
          </>
        )}
      </ListContainer>

      {showFetchMoreBtn && (
        <Button onClick={fetchMore} loading={isLoading}>
          Carregar mais ...
        </Button>
      )}

      <Modal isOpen={openModal}>
        <AddCourse onClose={handleModalState} refreshFeth={fetchData} />
      </Modal>

      <Modal isOpen={editingCourse !== null}>
        {editingCourse && (
          <AddCourse
            key={editingCourse.courseid}
            courseId={editingCourse.courseid}
            onClose={() => setEditingCourse(null)}
            refreshFeth={fetchData}
          />
        )}
      </Modal>

      <Confirm
        open={pendingCourse !== null}
        danger
        title="Inativar curso"
        description={`O curso "${pendingCourse?.title}" sumirá da home dos alunos. Ele continua nesta lista como Inativo e pode ser reativado pela edição.`}
        confirmLabel="Inativar"
        loading={!!deletingId}
        onConfirm={confirmDeactivate}
        onCancel={() => setPendingCourse(null)}
      />
    </>
  );
};

export default AdminCourses;
